import { pluralize } from '@/shared/lib/plural';

import type { FormField } from './formModel';
import styles from './DiffList.module.scss';

/**
 * Какие поля отличаются между двумя наборами значений — «на сайте» и «в
 * черновике», «в версии 3» и «сейчас». По полю: название, было, стало.
 * Значения — коротко и словами: «3 элемента», «да», текст без разметки.
 */
export function DiffList({
  fields,
  before,
  after,
  beforeLabel,
  afterLabel,
}: {
  fields: readonly FormField[];
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  beforeLabel: string;
  afterLabel: string;
}) {
  const changed = fields.filter((field) => !same(before[field.key], after[field.key]));
  const translationsChanged = !same(before['_i18n'] ?? {}, after['_i18n'] ?? {});

  if (changed.length === 0 && !translationsChanged) {
    return <p className={styles['empty']}>Отличий нет.</p>;
  }

  return (
    <ul className={styles['list']}>
      {changed.map((field) => (
        <li key={field.id} className={styles['item']}>
          <span className={styles['name']}>{field.name}</span>
          <span className={styles['side']}>
            <span className={styles['label']}>{beforeLabel}</span>
            <span className={styles['before']}>{preview(before[field.key])}</span>
          </span>
          <span className={styles['side']}>
            <span className={styles['label']}>{afterLabel}</span>
            <span className={styles['after']}>{preview(after[field.key])}</span>
          </span>
        </li>
      ))}
      {translationsChanged && (
        <li className={styles['item']}>
          <span className={styles['name']}>Переводы</span>
          <span className={styles['after']}>изменились</span>
        </li>
      )}
    </ul>
  );
}

function same(a: unknown, b: unknown): boolean {
  const empty = (v: unknown) =>
    v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

  if (empty(a) && empty(b)) {
    return true;
  }

  return JSON.stringify(a) === JSON.stringify(b);
}

function preview(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'да' : 'нет';
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'string') {
    const text = value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return text.length > 160 ? `${text.slice(0, 160)}…` : text;
  }

  if (Array.isArray(value)) {
    return pluralize(value.length, ['элемент', 'элемента', 'элементов']);
  }

  return 'изменено';
}
